/* eslint-disable max-len */
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';

import { catchError, map, shareReplay } from 'rxjs/operators';
import { BehaviorSubject, Observable, OperatorFunction, throwError as _throw } from 'rxjs';

import {
  FiltersData,
  FilterListInterface as IFilterList,
} from '../../interfaces';
import { AppOptionsService } from '../app-options';
import { ErrorsHandlingService } from '../errors-handling';

interface IPartnerQpsStatistic {
  incomingReal: number;
  outgoingReal: number;
  incomingStandart: number;
  outgoingStandart: number;
  validQps: number;
}

interface IRevenueRes {
  revenue: {
    trafficType: { today: any[], todayTotals: any, yesterday: any[], yesterdayTotals: any },
    adFormat: { today: any[], todayTotals: any, yesterday: any[], yesterdayTotals: any }
  }
}
@Injectable({
  providedIn: 'root',
})
export class DataManagerService {

  private apiURL;
  tokenRefreshAttempts = 0;
  apiLinkErrors: string[] = [];
  filterState: {
    sspId: number[],
    dspId?: number[],
    from: any,
    to: any,
    groupByDate: string,
    selectedEndpointSSP: number[],
    selectedEndpointDSP?: number[],
  } | null = null;

  filterStateSubject = new BehaviorSubject(null);

  constructor(
    private http: HttpClient,
    private options: AppOptionsService,
    private readonly errorsHandlingService: ErrorsHandlingService,
  ) {
    this.apiURL = this.options.getApiUrl();
  }

  public getFilters(filtersArray: { filters: string[] }): Observable<any> {
    return this.http.post(`${this.apiURL}/filter_values`, filtersArray)
      .pipe(catchError(this.errorsHandlingService.processError));
  }

  public getEndpointsForDropdowns(): Observable<IFilterList.EndpointsData> {
    const params = new HttpParams({ fromObject: { without_deleted: '1' } });

    return this.http
      .get<IFilterList.EndpointsData>(`${this.apiURL}/endpoints`, { params })
      .pipe(
        map((res) => ({
          dsp: (res?.dsp ?? []).map((item) => this.adaptEndpoint(item)),
          ssp: (res?.ssp ?? []).map((item) => this.adaptEndpoint(item)),
        })),
        catchError(this.errorsHandlingService.processError),
        shareReplay(1),
      );
  }

  public getCompaniesForDropdowns(): Observable<IFilterList.CompaniesDataRes> {
    const params = new HttpParams({ fromObject: { without_deleted: true } });

    return this.http
      .get<IFilterList.CompaniesDataRes>(`${this.apiURL}/endpoints/grouped-by-company`, { params })
      .pipe(
        map((res) => ({
          ssp: (res?.ssp ?? []).map((company) => this.adaptCompany(company)),
          dsp: (res?.dsp ?? []).map((company) => this.adaptCompany(company)),
        })),
        catchError(this.errorsHandlingService.processError),
        shareReplay(1),
      );
  }

  public getSideCompanies(): Observable<{ ssp: Array<{ id: number; name: string }>; dsp: Array<{ id: number; name: string }> }> {
    return this.http
      .get<{ ssp: Array<{ id: number; name: string }>; dsp: Array<{ id: number; name: string }> }>(`${this.apiURL}/company`)
      .pipe(catchError(this.errorsHandlingService.processError));
  }

  public getCompanyEndpoints(side: 'ssp' | 'dsp', companyId?: number): Observable<Array<{ id: number; name: string }>> {
    let url = '';
    if (companyId){
      url = `${this.apiURL}/company/endpoints/${side}/${companyId}`;
    } else {
      url = `${this.apiURL}/company/endpoints/${side}`;
    }

    return this.http
      .get<Array<{ id: number; name: string }>>(url)
      .pipe(catchError(this.errorsHandlingService.processError));
  }

  public getFiltersForDroppedRequests(): Observable<FiltersData> {
    return this.http.get<FiltersData>(`${this.apiURL}/filters_config`)
      .pipe(catchError(this.errorsHandlingService.processError));
  }

  public validateApiLinkUnique(link: string, side: string, endpointId: number): Observable<any> {
    return this.http.post(`${this.options.getApiUrl()}/endpoint/validate/${side}/unique`, { link: link, endpointId: endpointId })
      .pipe(catchError(this.errorsHandlingService.processError));
  }

  public validateApiLinkAsUrl(link: string): Observable<any> {
    return this.http.post(`${this.options.getApiUrl()}/endpoint/validate/url`, { link: link })
      .pipe(catchError(this.errorsHandlingService.processError));
  }

  public validateApiLinkContainsMacros(link: string): Observable<any> {
    return this.http.post(`${this.options.getApiUrl()}/endpoint/validate/macros`, { link: link })
      .pipe(catchError(this.errorsHandlingService.processError));
  }

  public validateApiLinkStatus200(link: string): Observable<any> {
    return this.http.post(`${this.options.getApiUrl()}/endpoint/validate/status200`, { link: link })
      .pipe(catchError(this.errorsHandlingService.processError));
  }

  public getPartnerQpsStatistic(): Observable<{ partperQps: IPartnerQpsStatistic, sspQps: any[], dspQps: any[] }> {
    return this.http.post<{ partperQps: IPartnerQpsStatistic, sspQps: any[], dspQps: any[] }>(`${this.options.getApiUrl()}/qps/limits`, null)
      .pipe(catchError(this.errorsHandlingService.processError));
  }

  public getRevenue(): Observable<IRevenueRes> {
    return this.http.post<IRevenueRes>(`${this.options.getApiUrl()}/revenue`, null)
      .pipe(catchError(this.errorsHandlingService.processError));
  }

  public sendInfoAboutBrowser(data: { userAgent: string; width: number; height: number; }): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(`${this.options.getApiUrl()}/info-browser`, data)
      .pipe(catchError(this.errorsHandlingService.processError));
  }

  public verifyTraffic(autologger: any, errFunc: OperatorFunction<any, unknown>): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.http.post<any>(
        `${this.options.getApiUrl()}/traffic-logger`,
        autologger,
        { headers: new HttpHeaders().set('Content-Type', 'application/json') })
        .pipe(errFunc).subscribe(resp => {
          resolve(resp);
        }, error => {
          this.errorsHandlingService.handleError(error);
          reject(error);
        });
    });
  }

  public getSeatAndTokenForSspEndpoint(): Observable<any> {
    return this.http.post(`${this.options.getApiUrl()}/ssp/endpoint/seat-and-token`, null,
      { headers: new HttpHeaders().set('Content-Type', 'application/json') },
    ).pipe(catchError(this.errorsHandlingService.processError));
  }

  public checkTechnicalWorks(): Observable<{ messageType: string; useShowAlert: string; }> {
    return this.http.get<{ messageType: string; useShowAlert: string; }>(
      `${this.options.getApiUrl()}/check-technical-works`,
      { headers: new HttpHeaders().set('Content-Type', 'application/json') },
    ).pipe(catchError(this.errorsHandlingService.processError));
  }

  public checkSSLExpiry(): Observable<{ data: { days: number; message: string; } }> {
    return this.http.get<{ data: { days: number; message: string; } }>(`${this.options.getApiUrl()}/alerts/ssl-expiry`)
      .pipe(catchError(this.errorsHandlingService.processError));
  }

  private adaptEndpoint(
    item: Partial<IFilterList.EndpointsGeneral> & { id: number; name?: string }
  ): IFilterList.EndpointsGeneral {
    return {
      ...item,
      id: item.id,
      value: item.id,
      label: item.name ?? (item as any).label ?? '',
      tooltip: item.name ?? (item as any).label ?? '',
      checked: false,
      disabled: (item as any).disabled ?? false,
    } as IFilterList.EndpointsGeneral;
  }

  private adaptCompany(
    company: Partial<IFilterList.CompaniesDataGeneral> & { id: number; name?: string; endpoints?: Array<Partial<IFilterList.CompaniesDataGeneral['endpoints'][number]> & { id: number; name?: string }> }
  ): IFilterList.CompaniesDataGeneral {
    return {
      ...(company as any),
      id: company.id,
      value: company.id,
      label: company.name ?? (company as any).label ?? '',
      checked: false,
      indeterminate: false,
      disabled: (company as any).disabled ?? false,
      endpoints: (company.endpoints ?? []).map((e) => this.adaptCompanyEndpoint(e as any)),
    } as IFilterList.CompaniesDataGeneral;
  }

  private adaptCompanyEndpoint(
    endpoint: Partial<IFilterList.CompaniesDataGeneral['endpoints'][number]> & { id: number; name?: string }
  ): IFilterList.CompaniesDataGeneral['endpoints'][number] {
    return {
      ...(endpoint as any),
      id: endpoint.id,
      value: endpoint.id,
      label: endpoint.name ?? (endpoint as any).label ?? '',
      tooltip: endpoint.name ?? (endpoint as any).label ?? '',
      checked: false,
      disabled: (endpoint as any).disabled ?? false,
    } as IFilterList.CompaniesDataGeneral['endpoints'][number];
  }
}
